// src/components/SubprojectPage.js
import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  collection,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  addDoc,
  deleteDoc,
  orderBy,
  query
} from 'firebase/firestore';
import {
  getDownloadURL,
  ref as storageRef,
  uploadBytesResumable,
  deleteObject
} from 'firebase/storage';
import { auth, db, storage } from '../firebaseConfig';
import { useAuthState } from 'react-firebase-hooks/auth';
import generatePdfFromImages from './generatePdfFromImages';
import Sidebar from './Sidebar';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import './Photos.css';

function SubprojectPage() {
  const { projectId, subprojectId } = useParams();
  const [subprojectName, setSubprojectName] = useState('');
  const [photos, setPhotos] = useState([]);
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const [selectMode, setSelectMode] = useState(false);
  const [editingCaption, setEditingCaption] = useState(null);
  const [role, setRole] = useState('engineer');
  const [user] = useAuthState(auth);
  const [isDownloading, setIsDownloading] = useState(false);
  const [uploadQueue, setUploadQueue] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});

  // NOTES
  const [subprojectNotes, setSubprojectNotes] = useState('');
  const [notesDraft, setNotesDraft] = useState('');
  const [editingNotes, setEditingNotes] = useState(false);
  const [savingNotes, setSavingNotes] = useState(false);
  const [editingPhotoNote, setEditingPhotoNote] = useState(null);
  const [photoNoteDraft, setPhotoNoteDraft] = useState('');
  const inputRef = useRef();
  const navigate = useNavigate();

  const MAX_BATCH = 30;
  const MAX_TOTAL_BYTES = 200 * 1024 * 1024;

  useEffect(() => {
    if (projectId && subprojectId) {
      fetchSubprojectData();
      fetchPhotos();
    }
  }, [projectId, subprojectId]);

  useEffect(() => {
    const fetchRole = async () => {
      if (user) {
        const userDocRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          const r = userDoc.data().role;
          if (['admin', 'engineer', 'owner'].includes(r)) setRole(r);
        }
      }
    };
    fetchRole();
  }, [user]);

  const fetchSubprojectData = async () => {
    const subRef = doc(db, 'projects', projectId, 'subprojects', subprojectId);
    const subDoc = await getDoc(subRef);
    if (subDoc.exists()) {
      const data = subDoc.data();
      setSubprojectName(data.name || '');
      setSubprojectNotes(data.notes || '');
      setNotesDraft(data.notes || '');
    }
  };

  const fetchPhotos = async () => {
    const photosRef = collection(db, 'projects', projectId, 'subprojects', subprojectId, 'photos');
    const q = query(photosRef, orderBy('timestamp', 'desc'));
    const snapshot = await getDocs(q);
    const list = snapshot.docs.map(doc => {
      const data = doc.data();
      // iOS writes the full-size link as `fullUrl`; the web app writes `url`.
      // Normalize so the rest of this component only has to know about `url`.
      const url = data.fullUrl || data.url || '';
      return { id: doc.id, ...data, url, fullUrl: data.fullUrl || data.url || '' };
    });
    setPhotos(list);
  };

  const handleFileInput = (e) => {
    const files = Array.from(e.target.files);
    enqueueFiles(files);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    enqueueFiles(files);
  };

  const enqueueFiles = (files) => {
    const totalSize = files.reduce((sum, file) => sum + file.size, 0);
    if (totalSize > MAX_TOTAL_BYTES) {
      alert('Total upload size exceeds 200MB. Please select fewer or smaller files.');
      return;
    }
    setUploadQueue(prev => [...prev, ...files]);
  };

  useEffect(() => {
    if (uploadQueue.length > 0) processUploadQueue();
  }, [uploadQueue]);

  const processUploadQueue = async () => {
    const queue = [...uploadQueue];
    setUploadQueue([]);

    for (let i = 0; i < queue.length; i += MAX_BATCH) {
      const batch = queue.slice(i, i + MAX_BATCH);
      await Promise.allSettled(batch.map(uploadSingleFile));
    }

    fetchPhotos();
  };

  const generateThumbnail = (file) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const scale = 500 / img.width;
        canvas.width = 500;
        canvas.height = img.height * scale;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(blob => {
          URL.revokeObjectURL(url);
          resolve(blob);
        }, 'image/jpeg');
      };
      img.onerror = reject;
      img.src = url;
    });
  };

  const uploadSingleFile = async (file) => {
    const filename = file.name;
    const path = `projects/${projectId}/subprojects/${subprojectId}/photos/${filename}`;
    const thumbPath = `projects/${projectId}/subprojects/${subprojectId}/photos/thumbs/${filename}`;
    const fileRef = storageRef(storage, path);
    const thumbRef = storageRef(storage, thumbPath);

    try {
      const uploadTask = uploadBytesResumable(fileRef, file);
      uploadTask.on('state_changed', snapshot => {
        const percent = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
        setUploadProgress(prev => ({ ...prev, [filename]: percent }));
      });

      await uploadTask;
      const url = await getDownloadURL(fileRef);

      const thumbBlob = await generateThumbnail(file);
      await uploadBytesResumable(thumbRef, thumbBlob);
      const thumbUrl = await getDownloadURL(thumbRef);

      const caption = filename.replace(/\.[^/.]+$/, '');
      await addDoc(collection(db, 'projects', projectId, 'subprojects', subprojectId, 'photos'), {
        url,
        thumbUrl,
        caption,
        filename,
        storagePath: path,
        timestamp: new Date()
      });
    } catch (err) {
      console.error(`Failed to upload ${filename}:`, err);
    }
  };

  const toggleSelection = (photo) => {
    const alreadySelected = selectedPhotos.some(p => p.id === photo.id);
    setSelectedPhotos(prev =>
      alreadySelected ? prev.filter(p => p.id !== photo.id) : [...prev, photo]
    );
  };

  const handleSaveNotes = async () => {
    setSavingNotes(true);
    const text = notesDraft.trim();
    try {
      const subRef = doc(db, 'projects', projectId, 'subprojects', subprojectId);
      await updateDoc(subRef, { notes: text });
      setSubprojectNotes(text);
      setEditingNotes(false);
    } catch (err) {
      console.error('Error saving notes:', err);
      alert('Could not save notes.');
    } finally {
      setSavingNotes(false);
    }
  };

  const commitPhotoNote = async (photoId) => {
    const newNote = photoNoteDraft;
    const current = photos.find(p => p.id === photoId);

    setEditingPhotoNote(null);

    // Nothing changed — skip the write entirely.
    if ((current?.note || '') === newNote) return;

    setPhotos(prev => prev.map(p => p.id === photoId ? { ...p, note: newNote } : p));
    try {
      const photoRef = doc(db, 'projects', projectId, 'subprojects', subprojectId, 'photos', photoId);
      await updateDoc(photoRef, { note: newNote });
    } catch (err) {
      console.error('Error updating photo note:', err);
      alert('Could not save that note.');
    }
  };

  const handleCaptionChange = async (photoId, newCaption) => {
    setPhotos(prev => prev.map(p => p.id === photoId ? { ...p, caption: newCaption } : p));
    try {
      const photoRef = doc(db, 'projects', projectId, 'subprojects', subprojectId, 'photos', photoId);
      await updateDoc(photoRef, { caption: newCaption });
    } catch (err) {
      console.error('Error updating caption:', err);
    }
  };

  const handleGeneratePDF = async () => {
    if (!selectedPhotos.length) return;
    try {
      const formatted = selectedPhotos.map((photo, index) => ({
        url: photo.fullUrl || photo.url,
        name: photo.filename || `IMG_${index + 1}`
      }));
      const captions = {};
      selectedPhotos.forEach(p => {
        const key = p.filename || '';
        captions[key] = p.caption || key.replace(/\.[^/.]+$/, '');
      });
      await generatePdfFromImages(formatted, subprojectName, captions);
    } catch (error) {
      console.error('PDF generation failed:', error);
      alert('Failed to generate PDF.');
    }
  };

  const handleDeleteSelectedPhotos = async () => {
    if (!window.confirm(`Delete ${selectedPhotos.length} photo(s)? This cannot be undone.`)) return;

    for (const photo of selectedPhotos) {
      try {
        const thumbRef = storageRef(storage, `projects/${projectId}/subprojects/${subprojectId}/photos/thumbs/${photo.filename}`);
        await deleteObject(thumbRef).catch(() => {});
        await deleteDoc(doc(db, 'projects', projectId, 'subprojects', subprojectId, 'photos', photo.id));
      } catch (err) {
        console.error(`Failed to delete ${photo.filename}:`, err);
      }
    }

    setPhotos(prev => prev.filter(p => !selectedPhotos.some(sp => sp.id === p.id)));
    setSelectedPhotos([]);
    setSelectMode(false);
  };

  const downloadAllPhotos = async () => {
    if (!photos.length) {
      alert("No photos available to download.");
      return;
    }

    setIsDownloading(true);
    const zip = new JSZip();
    const folder = zip.folder(subprojectName || 'Photos');

    for (const photo of photos) {
      try {
        const fileName = photo.filename || `${photo.id}.jpg`;
        const storagePath = photo.storagePath || `projects/${projectId}/subprojects/${subprojectId}/photos/${fileName}`;
        const url = await getDownloadURL(storageRef(storage, storagePath));
        const response = await fetch(url);
        if (!response.ok) continue;
        const blob = await response.blob();
        folder.file(fileName, blob);
      } catch (error) {
        console.error(`Failed to download ${photo.filename}:`, error);
      }
    }

    try {
      const content = await zip.generateAsync({ type: 'blob' });
      saveAs(content, `${subprojectName || 'Subproject'}_Photos.zip`);
      alert('Download complete. Check your Downloads folder.');
    } catch (err) {
      console.error('ZIP generation failed:', err);
      alert('Failed to generate ZIP file.');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="ProjectsPage" onDragOver={(e) => e.preventDefault()} onDrop={handleDrop}>
      <header>{subprojectName || 'Loading subproject...'}</header>

      <Sidebar
        role={role}
        buttons={[
          {
            label: isDownloading ? 'Zipping Photos...' : 'Download All Photos',
            action: downloadAllPhotos,
            special: true,
            disabled: isDownloading
          },
          {
            label: selectMode ? 'Cancel Selection' : 'Select Photos',
            action: () => setSelectMode(!selectMode),
            special: true
          },
          ...(selectMode ? [
            {
              label: `Generate PDF (${selectedPhotos.length})`,
              action: handleGeneratePDF,
              special: true
            },
            {
              label: `Delete Photos (${selectedPhotos.length})`,
              action: handleDeleteSelectedPhotos,
              special: true
            }
          ] : [])
        ]}
      />

      <div className="main-content">
        <div style={{ marginBottom: '1rem' }}>
          <button
            onClick={() => navigate(`/project/${projectId}`)}
            style={{
              backgroundColor: '#ccc',
              border: 'none',
              borderRadius: '5px',
              padding: '8px 16px',
              cursor: 'pointer',
              fontWeight: 'bold'
            }}
          >
            ← Back to Project
          </button>
        </div>

        <div style={{
          marginBottom: '1.5rem',
          padding: '1rem',
          border: '1px solid #ddd',
          borderRadius: '8px',
          background: '#fff'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '0.5rem'
          }}>
            <strong>Notes</strong>
            {!editingNotes && (
              <button
                onClick={() => { setNotesDraft(subprojectNotes); setEditingNotes(true); }}
                style={{
                  border: 'none',
                  borderRadius: '5px',
                  padding: '4px 12px',
                  cursor: 'pointer',
                  backgroundColor: '#ccc',
                  fontWeight: 'bold'
                }}
              >
                {subprojectNotes ? 'Edit' : 'Add'}
              </button>
            )}
          </div>

          {editingNotes ? (
            <>
              <textarea
                value={notesDraft}
                onChange={(e) => setNotesDraft(e.target.value)}
                rows={6}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  fontFamily: 'inherit',
                  fontSize: '0.95rem',
                  boxSizing: 'border-box'
                }}
              />
              <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.5rem' }}>
                <button
                  onClick={handleSaveNotes}
                  disabled={savingNotes}
                  style={{
                    border: 'none', borderRadius: '5px', padding: '6px 16px',
                    cursor: 'pointer', backgroundColor: '#ccc', fontWeight: 'bold'
                  }}
                >
                  {savingNotes ? 'Saving…' : 'Save'}
                </button>
                <button
                  onClick={() => { setNotesDraft(subprojectNotes); setEditingNotes(false); }}
                  style={{
                    border: 'none', borderRadius: '5px', padding: '6px 16px',
                    cursor: 'pointer', backgroundColor: '#eee'
                  }}
                >
                  Cancel
                </button>
              </div>
            </>
          ) : (
            <div style={{ whiteSpace: 'pre-wrap', color: subprojectNotes ? 'inherit' : '#888' }}>
              {subprojectNotes || 'No notes yet.'}
            </div>
          )}
        </div>

        <div style={{ marginBottom: '2rem', padding: '1rem', border: '2px dashed #ccc', borderRadius: '8px', background: '#f9f9f9', textAlign: 'center' }}>
          <label htmlFor="photo-upload" style={{ fontWeight: 'bold' }}>Drag & drop or select up to 30 photos:</label>
          <input
            ref={inputRef}
            id="photo-upload"
            type="file"
            accept="image/*"
            multiple
            onChange={handleFileInput}
            style={{ display: 'block', margin: '0.5rem auto' }}
          />
        </div>

        {Object.keys(uploadProgress).length > 0 && (
          <div className="upload-progress-list">
            {Object.entries(uploadProgress).map(([name, progress]) => (
              <div key={name} style={{ marginBottom: '0.5rem' }}>
                <strong>{name}</strong>
                <div className="progress-bar">
                  <div className="progress" style={{ width: `${progress}%` }} />
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="image-container">
          {photos.map((photo) => {
            const selected = selectedPhotos.some((p) => p.id === photo.id);
            const imageElement = (
              <img
                src={photo.thumbUrl?.startsWith('http') ? photo.thumbUrl : photo.url}
                alt={photo.filename}
                className="image-thumb"
                onError={(e) => {
                  e.target.onerror = null;
                  if (photo.url) e.target.src = photo.url;
                }}
              />
            );

            return (
              <div
                key={photo.id}
                className={`image-thumbnail ${selected ? 'selected' : ''}`}
                onClick={() => selectMode && toggleSelection(photo)}
              >
                {selectMode || !photo.url ? (
                  imageElement
                ) : (
                  <a
                    href={photo.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Open full-size image in a new tab"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {imageElement}
                  </a>
                )}

                <div className="image-caption">
                  {selectMode && (
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() => toggleSelection(photo)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  )}
                  {editingCaption === photo.id ? (
                    <input
                      type="text"
                      autoFocus
                      value={photo.caption || ''}
                      placeholder="Enter caption"
                      onChange={(e) => handleCaptionChange(photo.id, e.target.value)}
                      onBlur={() => setEditingCaption(null)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          setEditingCaption(null);
                        }
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />
                  ) : (
                    <span
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingCaption(photo.id);
                      }}
                      style={{ cursor: 'text' }}
                    >
                      {photo.caption || photo.filename}
                    </span>
                  )}

                </div>

                <div className="image-note" style={{ fontSize: '0.8rem' }}>
                  {editingPhotoNote === photo.id ? (
                    <textarea
                      autoFocus
                      rows={3}
                      value={photoNoteDraft}
                      placeholder="Add a note"
                      onChange={(e) => setPhotoNoteDraft(e.target.value)}
                      onBlur={() => commitPhotoNote(photo.id)}
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        width: '100%',
                        fontFamily: 'inherit',
                        fontSize: '0.8rem',
                        boxSizing: 'border-box'
                      }}
                    />
                  ) : (
                    <span
                      onClick={(e) => {
                        e.stopPropagation();
                        setPhotoNoteDraft(photo.note || '');
                        setEditingPhotoNote(photo.id);
                      }}
                      style={{
                        cursor: 'text',
                        whiteSpace: 'pre-wrap',
                        color: photo.note ? '#333' : '#aaa'
                      }}
                    >
                      {photo.note ? `📝 ${photo.note}` : '📝 Add note'}
                    </span>
                  )}

                  {typeof photo.latitude === 'number' &&
                   typeof photo.longitude === 'number' && (
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${photo.latitude},${photo.longitude}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={`Taken at ${photo.latitude.toFixed(6)}, ${photo.longitude.toFixed(6)} — open in Google Maps`}
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        marginLeft: '0.35rem',
                        textDecoration: 'none',
                        cursor: 'pointer'
                      }}
                    >
                      📍
                    </a>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default SubprojectPage;
